/* ============================================================
   CARINA — description-accordion.js
   Wandelt Überschriften (h2/h3/h4) innerhalb der Produktbeschreibung
   automatisch in auf-/zuklappbare Abschnitte um (z. B. für GPSR).
   Progressive Enhancement: Ohne JS bleibt die volle Beschreibung sichtbar.
   ============================================================ */

'use strict';

let __descAccUid = 0;

class DescriptionAccordion extends HTMLElement {
  connectedCallback() {
    if (this._enhanced) return;
    this._enhanced = true;

    const levels = (this.getAttribute('data-levels') || 'h2,h3')
      .toUpperCase().split(',').map((s) => s.trim()).filter(Boolean);
    const firstOpen = this.getAttribute('data-first-open') === 'true';
    const isHeading = (n) => n.nodeType === 1 && levels.indexOf(n.tagName) !== -1;

    const nodes = Array.from(this.childNodes);
    if (!nodes.some(isHeading)) return; // keine passenden Überschriften → nichts umbauen

    const uid = ++__descAccUid;
    const frag = document.createDocumentFragment();
    let i = 0;

    // Intro (alles vor der ersten Überschrift) bleibt unverändert sichtbar
    while (i < nodes.length && !isHeading(nodes[i])) {
      frag.appendChild(nodes[i]);
      i++;
    }

    let section = 0;
    while (i < nodes.length) {
      const heading = nodes[i];
      i++;

      // Inhalt bis zur nächsten Überschrift einsammeln
      const content = [];
      while (i < nodes.length && !isHeading(nodes[i])) {
        content.push(nodes[i]);
        i++;
      }

      // Leere Überschrift (kein zugänglicher Name) → Abschnitt unverändert übernehmen
      if (heading.textContent.trim() === '') {
        frag.appendChild(heading);
        content.forEach((n) => frag.appendChild(n));
        continue;
      }

      section++;
      const panelId = 'desc-acc-' + uid + '-' + section;
      const btnId = panelId + '-btn';
      const open = firstOpen && section === 1;

      const btn = document.createElement('button');
      btn.type = 'button';
      btn.className = 'desc-acc__button';
      btn.id = btnId;
      btn.setAttribute('aria-expanded', open ? 'true' : 'false');
      btn.setAttribute('aria-controls', panelId);
      const label = document.createElement('span');
      label.innerHTML = heading.innerHTML;
      btn.appendChild(label);
      btn.insertAdjacentHTML('beforeend',
        '<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.75" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M6 9l6 6 6-6"/></svg>');

      // Überschriften-Semantik erhalten (gut für SEO/Screenreader); Anker-ID übernehmen
      const h = document.createElement(heading.tagName);
      h.className = 'desc-acc__heading';
      if (heading.id) h.id = heading.id;
      h.appendChild(btn);

      const panel = document.createElement('div');
      panel.className = 'desc-acc__panel' + (open ? ' is-open' : '');
      panel.id = panelId;
      panel.setAttribute('role', 'region');
      panel.setAttribute('aria-labelledby', btnId);
      panel.inert = !open; // eingeklappt: raus aus Tab-Order + A11y-Tree
      const inner = document.createElement('div');
      inner.className = 'desc-acc__inner';
      content.forEach((n) => inner.appendChild(n));
      panel.appendChild(inner);

      btn.addEventListener('click', () => {
        const expanded = btn.getAttribute('aria-expanded') === 'true';
        btn.setAttribute('aria-expanded', String(!expanded));
        panel.classList.toggle('is-open', !expanded);
        panel.inert = expanded; // beim Zuklappen inert=true
      });

      frag.appendChild(h);
      frag.appendChild(panel);
    }

    this.textContent = '';
    this.appendChild(frag);
  }
}

if (!customElements.get('description-accordion')) {
  customElements.define('description-accordion', DescriptionAccordion);
}
